import { useEmailCampaignsList, useUpdateCampaignStatus } from '@/hooks/useEmailCampaigns'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { CAMPAIGN_STATUS_LABELS, CAMPAIGN_STATUS_COLORS } from '@/types/emailCampaigns'
import { Mail, Play, Pause, X, Eye } from 'lucide-react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

interface Props {
  onNew: () => void
  onView: (id: string) => void
}

export default function CampaignsList({ onNew, onView }: Props) {
  const { data: campaigns, isLoading } = useEmailCampaignsList()
  const updateStatus = useUpdateCampaignStatus()

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-brown-900">Campagnes email</h2>
        <Button onClick={onNew}>
          <Mail className="w-4 h-4 mr-2" />
          Nouvelle campagne
        </Button>
      </div>

      {!campaigns?.length ? (
        <EmptyState
          icon={<Mail className="w-8 h-8" />}
          title="Aucune campagne"
          description="Crée ta première campagne pour démarrer la prospection email."
        />
      ) : (
        <Card padding="none">
          <table className="w-full">
            <thead className="text-xs text-brown-500 uppercase border-b border-brown-100">
              <tr>
                <th className="px-4 py-3 text-left">Nom</th>
                <th className="px-4 py-3 text-left">Statut</th>
                <th className="px-4 py-3 text-left">Progression</th>
                <th className="px-4 py-3 text-left">Cadence</th>
                <th className="px-4 py-3 text-left">Créée</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const pct = c.total_count > 0 ? Math.round((c.sent_count / c.total_count) * 100) : 0
                return (
                  <tr key={c.id} className="border-t border-brown-100">
                    <td className="px-4 py-3 text-sm font-medium text-brown-900">{c.name}</td>
                    <td className="px-4 py-3">
                      <Badge variant={CAMPAIGN_STATUS_COLORS[c.status]}>
                        {CAMPAIGN_STATUS_LABELS[c.status]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-1.5 bg-brown-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-mint-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-brown-600">
                          {c.sent_count}/{c.total_count}
                          {c.failed_count > 0 ? ` (${c.failed_count} échec)` : ''}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-brown-600">
                      {c.throttle_per_day}/j • {c.send_window_start}-{c.send_window_end}
                    </td>
                    <td className="px-4 py-3 text-sm text-brown-500">
                      {format(new Date(c.created_at), 'd MMM HH:mm', { locale: fr })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => onView(c.id)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {c.status === 'draft' && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() =>
                              updateStatus.mutate({ id: c.id, status: 'running' })
                            }
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Lancer
                          </Button>
                        )}
                        {c.status === 'running' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              updateStatus.mutate({ id: c.id, status: 'paused' })
                            }
                          >
                            <Pause className="w-4 h-4" />
                          </Button>
                        )}
                        {c.status === 'paused' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              updateStatus.mutate({ id: c.id, status: 'running' })
                            }
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                        )}
                        {(c.status === 'draft' || c.status === 'running' || c.status === 'paused') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm('Annuler cette campagne ?')) {
                                updateStatus.mutate({ id: c.id, status: 'cancelled' })
                              }
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  )
}
